import type { Price } from '@misumi/shared'
import type { PriceRange, VolumePricingTier } from './types'

/** `$` for USD, `"<CODE> "` for anything else — shared by `formatPrice` and
 * `formatSubtotal` so the currency-symbol branch lives in exactly one
 * place (code-review UID-47 L2). */
function currencySymbol(currency: string): string {
  return currency === 'USD' ? '$' : `${currency} `
}

/** Minimal display formatting for a `Price`-shaped value — no
 * currency-conversion or locale logic here, just what the table row needs.
 * Typed on `amount`/`currency` only so it also formats a provenance-less
 * illustrative demo price (UID-222 #3, ADR-0066) without claiming it is a
 * validated `Price` value object. */
export function formatPrice(price: Pick<Price, 'amount' | 'currency'>): string {
  const amount = Number.parseFloat(price.amount)
  const formatted = Number.isFinite(amount) ? amount.toFixed(2) : price.amount
  return `${currencySymbol(price.currency)}${formatted}`
}

/**
 * Per-row subtotal (qty × unit price) for the assembly tree's "per-row
 * qty/price/subtotal" column (components.md "Assembly tree (BOM)"). Not a
 * validated `Price` value object — it's a display-only derived total, so it
 * skips `makePrice`'s round-trip and just formats the arithmetic directly,
 * matching `formatPrice`'s own $-symbol/2-decimal convention. Renders an
 * em-dash for quote-required/locked lines that don't have a numeric
 * `indicativePrice` yet.
 */
export function formatSubtotal(price: Price | undefined, quantity: number): string {
  if (!price) return '—'
  const amount = Number.parseFloat(price.amount)
  if (!Number.isFinite(amount)) return '—'
  const total = amount * quantity
  return `${currencySymbol(price.currency)}${total.toFixed(2)}`
}

/**
 * UID-225 (Parts Library visual-fidelity pass) — the richer "$34–$56"
 * UNIT PRICE cell text for a `ready` row whose `pricingProjection.volumeCurve`
 * has more than one rung (design ref: `srcn-part-library-listing-page.png`
 * row 1). Derived ENTIRELY from the real `VolumePricingCurve.tiers` already
 * on the wire (ADR-0058) — never a fabricated spread. A single-tier curve
 * (or one whose amounts happen to be equal) collapses to one price, matching
 * `formatPrice`'s plain output rather than a degenerate "$X–$X" range.
 */
export function formatPriceRange(
  currency: string,
  tiers: readonly Pick<VolumePricingTier, 'unitPriceAmount'>[],
): string | null {
  const amounts = tiers
    .map((tier) => Number.parseFloat(tier.unitPriceAmount))
    .filter((amount) => Number.isFinite(amount))
  if (amounts.length === 0) return null
  const min = Math.min(...amounts)
  const max = Math.max(...amounts)
  const minLabel = formatPrice({ amount: String(min), currency })
  if (min === max) return minLabel
  const maxLabel = formatPrice({ amount: String(max), currency })
  return `${minLabel}–${maxLabel}`
}

/**
 * Formats a `PriceRange { low, high }` value (a pre-computed low/high pair
 * already on the wire, e.g. a quote's `priceRange` or `pricingRange`) into
 * a "$X" or "$X–$Y" headline — collapsing to the single price when
 * `low === high`. Deliberately named `formatPriceBand` (not
 * `formatPriceRange`) to avoid colliding with the tiers-based
 * `formatPriceRange(currency, tiers)` above, which derives its own min/max
 * from a `VolumePricingCurve` rather than taking a pre-computed range
 * (code-review UID-332 dedup — was triplicated across `PartDetailPage` and
 * `PartActiveQuotesSection` under two different names/signatures).
 */
export function formatPriceBand(range: PriceRange | null | undefined): string {
  if (!range) return '—'
  const low = formatPrice(range.low)
  const high = formatPrice(range.high)
  return low === high ? low : `${low}–${high}`
}

/**
 * The Requirements cell's "material · tolerance · finish" detail line
 * (design ref: same screenshot) — joins whichever of these are actually
 * present on the row (`sourceable.configAttributes` / `dfmSummary`) and
 * omits the rest; returns `null` (render nothing) rather than an empty
 * string when none are present, so callers don't render a stray line.
 */
export function formatRequirementsDetail(
  parts: readonly (string | null | undefined)[],
): string | null {
  const clean = parts.filter((part): part is string => Boolean(part && part.trim().length > 0))
  return clean.length > 0 ? clean.join(' · ') : null
}

export function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * UID-378 — ISO `YYYY-MM-DD` date formatter for the BOM card grid's meta row
 * (measured design spec shows the raw ISO date, right-aligned, not
 * `formatDate`'s localized "Aug 28, 2026"). Scoped to just that one caller
 * (`BomCardGrid.tsx`) — `formatDate` itself is untouched since other screens
 * already depend on its localized output.
 */
export function formatDateIso(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toISOString().slice(0, 10)
}

/**
 * Full-month-name variant of `formatDate` (e.g. "August 19, 2025") for the
 * "Latest purchased revision" banner's prose sentence (`dc.html:1769`,
 * "This revision was last purchased on {date}") — the surrounding cells use
 * the abbreviated `formatDate`, but the prototype's banner sentence reads
 * naturally with the full month name.
 *
 * Formatting is pinned to `en-US` AND `UTC` so the output is identical on
 * every machine:
 *  - Locale `en-US` matches the design's US-format prose ("August 19, 2025");
 *    `toLocaleDateString(undefined, …)` follows the runtime's default locale,
 *    rendering "19 August 2025" under e.g. `en-IN`/`en-GB`.
 *  - `timeZone: 'UTC'` keeps the calendar day stable. `orderedOn` is a
 *    UTC-anchored date (e.g. `2025-08-19T00:00:00Z`); formatting it in a
 *    negative-offset local zone (e.g. UTC−4) would roll it back to the
 *    previous day ("August 18, 2025"). It represents the day of purchase, not
 *    a local instant, so it must render in UTC everywhere.
 */
export function formatLongDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * UID-577 — the drawing/datasheet filename shown as the chip under a Parts
 * Library row's part name.
 *
 * Mirrors the design's `drawingInfo` (`Fictiv Platform.dc.html:7479-7491`),
 * whose asymmetry is the whole point and was the actual bug:
 *
 *   - a CUSTOM (Make) part surfaces a drawing ONLY if one was really
 *     uploaded — the design's own comment is "never fabricate one";
 *   - a STANDARD/catalog (Buy) part ALWAYS has a datasheet, so when no
 *     filename came from the server the design DERIVES a stable display name
 *     from the part number (`ABC-123` -> `ABC-123 datasheet.pdf`), falling
 *     back to the first token of the part name when there is no usable MPN.
 *
 * The build previously rendered the chip only when `drawingFilename` was
 * populated, which is correct for Make rows and silently dropped the chip on
 * every Buy row — the "PDF name missing" half of UID-577.
 *
 * Returns `null` when there is no name to show, so the caller's conditional
 * stays a simple truthiness check.
 */
export function resolveDrawingName(input: {
  readonly isCustom: boolean
  readonly drawingFilename: string | null
  readonly catalogSku: string | null | undefined
  readonly name: string | null | undefined
}): string | null {
  const { isCustom, drawingFilename, catalogSku, name } = input
  if (drawingFilename) return drawingFilename
  // Custom parts: no upload, no chip. Never synthesized.
  if (isCustom) return null
  // `—` and "no MPN" are the design's two placeholder part numbers — neither
  // is a real MPN, so neither may become a filename.
  const sku = catalogSku?.trim() ?? ''
  const hasMpn = sku !== '' && !/no MPN|—/i.test(sku)
  if (hasMpn) return `${sku.replace(/[^A-Za-z0-9]+/g, '-')} datasheet.pdf`
  const firstToken = (name ?? 'PART').split(/[ ,—]/)[0] ?? ''
  const stem = firstToken === '' ? 'PART' : firstToken.toUpperCase()
  return `${stem} datasheet.pdf`
}

/**
 * UID-577 — the availability sub-line under a Buy row's configuration in the
 * Parts Library REQUIREMENTS cell.
 *
 * Mirrors the design's `reqSubArr` catalog branch (`Fictiv Platform.dc.html
 * :8640-8643`):
 *
 *     leadStock = !isCustom && /in ?stock/i.test(p.lead||'')
 *     stockCap  = p.volCap || 500
 *     sub       = leadStock ? ('In stock · up to '+stockCap+' available')
 *                           : p.lead
 *
 * `inStock` is reported separately from the text because the design styles an
 * in-stock line differently (teal/semibold vs the muted default) — the caller
 * needs the fact, not a regex over the returned string.
 *
 * ONE DELIBERATE DEPARTURE from the design: it defaults the ceiling to a
 * hardcoded `500` when the part has no volume cap. A specific number is an
 * availability CLAIM, so it is only shown here when a real `cap` backs it;
 * otherwise the line degrades to a bare "In stock". Returns `null` when there
 * is no availability information at all, so no row asserts one.
 */
export function formatStockAvailability(input: {
  readonly leadTime: string | null | undefined
  readonly volumeCap: number | null | undefined
}): { readonly text: string; readonly inStock: boolean } | null {
  const lead = input.leadTime?.trim() ?? ''
  if (lead === '') return null
  if (!/in ?stock/i.test(lead)) return { text: lead, inStock: false }
  const cap = input.volumeCap
  if (cap == null || !Number.isFinite(cap) || cap <= 0) {
    return { text: 'In stock', inStock: true }
  }
  return { text: `In stock · up to ${cap.toLocaleString('en-US')} available`, inStock: true }
}
